/**
 * Koš: smazaná stanoviště, včelstva a zápisy. Dají se obnovit, nebo koš vysypat.
 */
import { useState } from 'react';
import type { ColonyEvent } from '../db/model';
import { emptyTrash, readTrash, type TrashStore } from '../db/db';
import { setDeleted } from '../db/actions';
import { useQuery } from './hooks';
import { colonyName, EVENT_LABEL, fmtDate } from './labels';
import { describeEvent } from './describe';
import { Empty, Notice, Screen, Section, toast } from './ui';
import { saved } from './confirm';
import { EventIcon, Icon } from './icons';

export function TrashScreen() {
  const data = useQuery(readTrash, []);
  const [confirm, setConfirm] = useState(false);
  if (!data) return <Screen title="Koš" backTo="nastaveni">{null}</Screen>;
  const { apiaries, colonies, events, allApiaries, allColonies } = data;
  const apName = (id: string) => allApiaries.find((a) => a.id === id)?.name ?? '?';
  const colName = (id: string) => colonyName(allColonies.find((c) => c.id === id)?.label) ?? '?';
  const total = apiaries.length + colonies.length + events.length;
  const restore = async (store: TrashStore, id: string, what: string) => {
    await setDeleted(store, id, false);
    saved(`${what} obnoveno`);
  };
  const byDeleted = <T extends { deletedAt?: string | null }>(a: T, b: T) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? '');

  return (
    <Screen title="Koš" subtitle={total ? `${total} ${total === 1 ? 'položka' : total < 5 ? 'položky' : 'položek'}` : undefined} crumbs={[['Nastavení', 'nastaveni']]} backTo="nastaveni">
      <p className="muted">Smazané věci zůstávají tady, dokud koš nevysypete. Ťuknutím na „Obnovit“ je vrátíte zpět i s historií.</p>
      {total === 0 && <Empty>Koš je prázdný.</Empty>}

      {apiaries.length > 0 && (
        <Section title="Stanoviště">
          <ul className="list">
            {apiaries.sort(byDeleted).map((a) => (
              <li key={a.id} className="trash-row">
                <span className="grow">
                  <strong>{a.name}</strong>
                  <span className="small muted"> · smazáno {fmtDate(a.deletedAt)}</span>
                </span>
                <button className="btn small" onClick={() => restore('apiaries', a.id, 'Stanoviště')}>
                  Obnovit
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {colonies.length > 0 && (
        <Section title="Včelstva">
          <ul className="list">
            {colonies.sort(byDeleted).map((c) => (
              <li key={c.id} className="trash-row">
                <span className="grow">
                  <strong>{colonyName(c.label)}</strong>
                  <span className="small muted">
                    {' '}
                    · {apName(c.apiaryId)} · smazáno {fmtDate(c.deletedAt)}
                  </span>
                </span>
                <button className="btn small" onClick={() => restore('colonies', c.id, 'Včelstvo')}>
                  Obnovit
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {events.length > 0 && (
        <Section title="Zápisy">
          <ul className="list">
            {(events as ColonyEvent[]).sort(byDeleted).map((e) => (
              <li key={e.id} className="trash-row">
                <span className="trash-icon" aria-hidden>
                  <EventIcon type={e.type} size={22} />
                </span>
                <span className="grow">
                  <strong>
                    {EVENT_LABEL[e.type]} · {fmtDate(e.date)}
                  </strong>
                  <span className="small muted">
                    {' '}
                    · {e.colonyIds.map(colName).join(', ')}
                  </span>
                  <span className="small muted trash-desc">{describeEvent(e, { apiary: apName, colony: colName })}</span>
                </span>
                <button className="btn small" onClick={() => restore('events', e.id, 'Zápis')}>
                  Obnovit
                </button>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {total > 0 &&
        (confirm ? (
          <Notice tone="warn">
            Opravdu <strong>natrvalo smazat</strong> všech {total} položek v koši? Tohle už nejde vrátit.
            <div className="quick-row" style={{ marginTop: 10 }}>
              <button
                className="btn danger"
                onClick={async () => {
                  const kept = await emptyTrash();
                  setConfirm(false);
                  saved('Koš vysypán');
                  if (kept)
                    setTimeout(
                      () => toast(`${kept} ${kept === 1 ? 'položka zůstala' : 'položky zůstaly'} v koši – patří k nim zápisy (např. evidence léčiv).`),
                      1400,
                    );
                }}
              >
                Ano, vysypat koš
              </button>
              <button className="btn" onClick={() => setConfirm(false)}>
                Zrušit
              </button>
            </div>
          </Notice>
        ) : (
          <button className="btn danger wide" onClick={() => setConfirm(true)}>
            <Icon name="warning" size={20} /> Vysypat koš
          </button>
        ))}
    </Screen>
  );
}
